// frontend/src/components/VoiceRecorder/WaveformVisualizer.js
import React, { useRef, useEffect } from 'react';
import './WaveformVisualizer.css';

const WaveformVisualizer = ({ isRecording, isPaused, stream }) => {
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (stream && isRecording && !isPaused) {
      initializeAudioContext();
    } else if (isPaused || !isRecording) {
      stopVisualization();
    }

    return () => {
      stopVisualization();
      cleanupAudioContext();
    };
  }, [stream, isRecording, isPaused]);

  const initializeAudioContext = () => {
    if (!stream) return;

    try {
      // Create audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      
      // Connect stream to analyser
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      // Configure analyser
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;
      
      // Start visualization
      drawWaveform();
    } catch (error) {
      console.error('Error initializing audio context:', error);
    }
  };

  const drawWaveform = () => {
    if (!canvasRef.current || !analyserRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!isRecording || isPaused) {
        cancelAnimationFrame(animationRef.current);
        return;
      }

      animationRef.current = requestAnimationFrame(draw);
      
      // Get frequency data
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Clear canvas
      ctx.fillStyle = 'rgba(240, 242, 247, 0.95)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Calculate bar dimensions
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;
      
      // Draw frequency bars
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
        
        // Create gradient for bars
        const gradient = ctx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
        gradient.addColorStop(0, '#4F46E5');
        gradient.addColorStop(0.5, '#7C3AED');
        gradient.addColorStop(1, '#A855F7');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + 1;
      }
    };

    draw();
  };

  const stopVisualization = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    // Clear canvas
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const cleanupAudioContext = () => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  return (
    <div className="waveform-visualizer">
      <canvas 
        ref={canvasRef}
        width={400}
        height={100}
        className="waveform-canvas"
      />
      {isPaused && (
        <div className="paused-overlay">
          <span>Paused</span>
        </div>
      )}
    </div>
  );
};

export default WaveformVisualizer;